import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { markJustLoggedIn } from './welcome';
import {
  KINGSTON, getActiveSite, storeActiveSite, allowedSites, resolveSite, roleForSite, switchSite,
  watchSiteChanges, reconcileSiteWithServer,
} from './sites';

const AuthContext = createContext(null);

// Supabase-backend mode (set at cutover). When on, "authed" must reflect a real
// Supabase session — not just a stale 3c_token — or a staff member who logged in
// pre-cutover would skip login and hit an empty (RLS-blocked) app.
const SUPABASE_MODE = process.env.REACT_APP_BACKEND === 'supabase';

// Server-authoritative session (Phase E, 2026-04-21).
// The Apps Script backend issues a 1h sliding-TTL session token per
// login. Every authenticated request refreshes that TTL server-side.
// On expiry, the server returns {authRequired:true} and api.js's
// _handleAuthFailure wipes localStorage and redirects to /.
// The client no longer tracks its own expiry — presence of a token
// is the client-side truth; the server decides whether it's still valid.

function hasToken() {
  try { return Boolean(localStorage.getItem('3c_token')); }
  catch { return false; }
}

function getStoredRole() {
  try {
    if (!hasToken()) return null;
    return localStorage.getItem('3c_role') || null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [role, setRole]     = useState(() => getStoredRole());
  const [authed, setAuthed] = useState(() => hasToken());
  // Active location + the set this account may enter. `role` above is always
  // the role FOR the active location — Ric is `hd` at Albany and `detailer` at
  // Kingston off one login, so role and site move together and every existing
  // `role ===` check in the app keeps working untouched.
  const [site, setSite]   = useState(() => getActiveSite());
  const [sites, setSites] = useState(() => [getActiveSite()]);
  // True when this login holds the SAME role at every shop it may enter, so
  // switching location changes the data but never the profile. SiteSwitcher
  // uses it to offer the swap to HD (hd at both shops since 2026-09-08).
  const [sameRoleEverywhere, setSameRoleEverywhere] = useState(false);
  const reconciledRef = useRef(false);

  // A shop change in ANOTHER tab, window or the installed PWA writes
  // localStorage and this document would otherwise carry on rendering the
  // previous shop under the new label. `storage` fires only in the other
  // documents, which is exactly the case that needs catching.
  useEffect(() => watchSiteChanges(), []);

  // In Supabase mode the actual Supabase session is the source of truth for
  // "authed" (a stale 3c_token alone means nothing — no session → RLS blocks
  // everything). supabase-js auto-refreshes the access token from the long-
  // lived refresh token, so a returning user with a live session stays in
  // WITHOUT re-PINning. Only a genuine sign-out / refresh-token expiry clears
  // auth and returns the user to the PIN.
  //
  // IMPORTANT: do NOT touch 3c_token here. 3c_token holds the legacy *GAS*
  // session token (minted by gasVerifyPin at login). GAS validates against its
  // own session store and rejects a Supabase JWT, so mirroring the Supabase
  // access token here would break every still-legacy GAS endpoint (engineer
  // data, 3CAM, checklist). The legacy-auth-failure path is already a no-op in
  // Supabase mode (see apiLegacy), so an expired GAS token never logs you out.
  useEffect(() => {
    if (!SUPABASE_MODE) return undefined;
    let active = true;
    const sync = (session) => {
      if (!active) return;
      if (session) {
        const user = session.user;
        // Clamp the stored location to what this JWT actually permits. A stale
        // '3c_site' from a previous login (or an account that lost Albany
        // access) must never leave the UI labelled for a shop Postgres will
        // refuse to serve — that reads as "the app is broken", not "no access".
        const resolved = resolveSite(user, getActiveSite());
        const allowed = allowedSites(user);
        if (resolved !== getActiveSite()) {
          // The header was already sent with the WRONG location when the client
          // was constructed, so every read this page-load would be mis-scoped.
          // Persist the correction and reload rather than limp on.
          storeActiveSite(resolved);
          try { window.location.reload(); } catch (_) {}
          return;
        }
        const r = roleForSite(user, resolved);
        setAuthed(true);
        setSite(resolved);
        setSites(allowed);
        setSameRoleEverywhere(allowed.length > 1 && allowed.every((s) => roleForSite(user, s) === r));
        if (r) { setRole(r); try { localStorage.setItem('3c_role', r); } catch (_) {} }
        // 🚨 THE GATE. Everything above is what this CLIENT believes. auth_site()
        // is what Postgres will actually scope every read to, so it is the only
        // authority on which shop the data came from. Disagreeing with it means
        // the tag on screen is lying about the rows underneath it — the exact
        // complaint this exists to answer. Adopt the truth and reload rather
        // than keep painting a label we cannot stand behind.
        //
        // Once per session: it is a cheap RPC, but it must not run on every
        // token refresh.
        if (!reconciledRef.current) {
          reconciledRef.current = true;
          reconcileSiteWithServer(supabase, allowed).catch(() => {});
        }
      } else {
        setAuthed(false); setRole(null);
      }
    };
    supabase.auth.getSession().then(({ data }) => sync(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => sync(session));
    return () => { active = false; try { sub.subscription.unsubscribe(); } catch (_) {} };
  }, []);

  // `siteSlug` is the location the PIN signed into — its account's home shop.
  // DevLogin has already persisted it (the Supabase client had to be rebuilt
  // with the right header), so this just brings React state in line.
  function login(r, token, siteSlug) {
    setRole(r);
    setAuthed(true);
    if (siteSlug) setSite(siteSlug);
    markJustLoggedIn(); // one-shot flag → <WelcomeGreeting> fires on the next screen
    try {
      localStorage.setItem('3c_role', r);
      localStorage.setItem('3c_last_activity', String(Date.now())); // fresh idle clock (see IdleLogout)
      if (token) localStorage.setItem('3c_token', token);
    } catch {}
  }

  // Change the working location. Full page reload — see switchSite().
  function goToSite(slug, opts) {
    if (!sites.includes(slug)) return;
    switchSite(slug, opts);
  }

  async function logout() {
    setRole(null);
    setAuthed(false);
    // Back to the default location so the next PIN on this device doesn't
    // inherit the previous user's shop.
    try { storeActiveSite(KINGSTON); } catch (_) {}
    // AWAIT the sign-out. Fire-and-forget left a window where the Supabase
    // session was still in its own storage key while we'd already cleared
    // ours — so a reload landing in that window found a live session via
    // getSession() and silently signed the user back in.
    if (SUPABASE_MODE) { try { await supabase.auth.signOut(); } catch (_) {} }
    try {
      localStorage.removeItem('3c_role');
      localStorage.removeItem('3c_token');
      localStorage.removeItem('3c_last_activity');
      localStorage.removeItem('3c_site');
      // Clean up legacy keys from the pre-Phase-E client session
      localStorage.removeItem('3c_auth');
      localStorage.removeItem('3c_expiry');
    } catch {}
  }

  return (
    <AuthContext.Provider value={{ role, authed, login, logout, site, sites, goToSite, sameRoleEverywhere }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
